import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";
import { getPostBySlug, getRelatedPosts, type BlogBlock } from "@/content/blog";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";

const renderBlock = (block: BlogBlock, idx: number) => {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={idx}
          className="font-display uppercase text-white text-2xl md:text-3xl mt-14 mb-4 leading-tight"
        >
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={idx}
          className="font-display uppercase text-gold text-lg md:text-xl mt-10 mb-3 tracking-wide"
        >
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p key={idx} className="text-silver text-base md:text-lg leading-relaxed mt-5">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={idx} className="mt-6 space-y-3">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-silver text-base md:text-lg leading-relaxed">
              <span className="text-gold font-display mt-1.5 flex-shrink-0">✦</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote
          key={idx}
          className="my-10 border-l-2 border-gold/60 pl-6 py-2 bg-navy-elevated/40"
        >
          <p className="font-display text-white text-xl md:text-2xl italic leading-snug">
            "{block.text}"
          </p>
          {block.cite && (
            <cite className="not-italic block mt-3 text-xs uppercase tracking-[0.3em] text-silver/80">
              — {block.cite}
            </cite>
          )}
        </blockquote>
      );
    case "cta": {
      const primary = block.kind !== "ghost";
      return (
        <div key={idx} className="mt-8 mb-2">
          <Link
            to={block.to}
            onClick={() =>
              trackCTA(block.to === "/inquire" ? "inquire_cta" : "books_cta", {
                surface: "blog_post_inline",
              })
            }
            className={
              primary
                ? "group inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-4 shadow-cta hover:brightness-110 transition-all"
                : "group inline-flex items-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-4 hover:bg-gold/10 hover:border-gold transition-all"
            }
          >
            {block.label}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      );
    }
  }
};

const BlogPost = () => {
  const { slug = "" } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post.slug, 3);
  const url = `https://drsullyspeaks.com/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title={`${post.title} | Dr. Sully`}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.description,
          url,
          mainEntityOfPage: url,
          datePublished: post.publishedISO,
          dateModified: post.updatedISO ?? post.publishedISO,
          keywords: post.keywords.join(", "),
          articleSection: post.category,
          author: {
            "@type": "Person",
            name: "Dr. RD 'Sully' Sullivan",
            url: "https://drsullyspeaks.com/about",
          },
          publisher: {
            "@type": "Organization",
            name: "DrSullySpeaks",
            logo: {
              "@type": "ImageObject",
              url: "https://drsullyspeaks.com/favicon.png",
            },
          },
        }}
      />
      <SiteNav />

      <main id="main">
        {/* HEADER */}
        <section className="relative pt-36 md:pt-44 pb-10 md:pb-14 bg-hero overflow-hidden">
          <div className="container max-w-3xl">
            <Reveal variant="up">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-silver hover:text-white transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All essays
              </Link>
              <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-silver/80">
                <span className="text-gold">{post.category}</span>
                <span>·</span>
                <span>{post.readMinutes} min read</span>
                <span>·</span>
                <time dateTime={post.publishedISO}>
                  {new Date(post.publishedISO).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </time>
              </div>
              <h1 className="font-display font-bold uppercase text-white text-3xl md:text-5xl lg:text-6xl mt-5 leading-[1.05]">
                {post.title}
              </h1>
              <p className="text-silver text-lg md:text-xl mt-6 leading-relaxed italic">
                {post.hook}
              </p>
            </Reveal>
          </div>
        </section>

        {/* BODY */}
        <article className="py-10 md:py-16">
          <div className="container max-w-3xl">
            <Reveal variant="fade">
              <div>{post.body.map(renderBlock)}</div>
            </Reveal>
          </div>
        </article>

        {/* NEWSLETTER CAPTURE */}
        <section className="pb-16 md:pb-20">
          <div className="container max-w-2xl">
            <Reveal variant="up">
              <LeadMagnetForm sourcePage={`blog-post:${post.slug}`} />
            </Reveal>
          </div>
        </section>



        {/* RELATED */}
        {related.length > 0 && (
          <section className="py-16 border-t border-border bg-navy-deep">
            <div className="container max-w-5xl">
              <span className="eyebrow">Keep Reading</span>
              <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-3 mb-8">
                Related essays
              </h2>
              <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
                {related.map((r) => (
                  <li key={r.slug} className="bg-navy hover:bg-navy-elevated transition-colors">
                    <Link
                      to={`/blog/${r.slug}`}
                      className="block p-8 group focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
                    >
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        {r.category}
                      </div>
                      <h3 className="font-display uppercase text-white text-lg md:text-xl mt-3 leading-tight group-hover:text-gradient-gold transition-all">
                        {r.title}
                      </h3>
                      <p className="text-silver text-sm mt-3 leading-relaxed line-clamp-3">
                        {r.description}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="py-20 md:py-24 border-t border-border">
          <div className="container max-w-3xl text-center">
            <span className="eyebrow !mx-auto">Take the next step</span>
            <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-4 leading-tight">
              Bring the change.<br />
              <span className="text-gradient-gold">We'll build the plan.</span>
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/inquire"
                onClick={() => trackCTA("inquire_cta", { surface: "blog_post_footer" })}
                className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 shadow-cta hover:brightness-110 transition-all"
              >
                Work With Dr. Sully
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/books"
                onClick={() => trackCTA("books_cta", { surface: "blog_post_footer" })}
                className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/10 hover:border-gold transition-all"
              >
                Browse the Books
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default BlogPost;
